import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';

import { customSlimScroll } from '@controlrooms/web/src/app/global-styles';

export interface TimePickerProps {
  value: string; // Format: "HH:mm:ss"
  onChange: (time: string) => void;
  timeFormat?: '12h' | '24h';
  label?: string;
  includeSeconds?: boolean;
}

const TimeInputContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const TimeColumns = styled.div`
  display: flex;
  gap: 0;
  margin-bottom: 16px;
`;

const TimeColumn = styled.div`
  display: flex;
  flex-direction: column;̆
  align-items: center;
  flex: 1;
`;

const ColumnLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.datePickerv2.timeInputLabel.color};
  margin-bottom: 8px;
  text-align: center;
`;

const TimeValues = styled.div`
  max-height: 200px;
  overflow-y: auto;
  background: ${({ theme }) => theme.datePickerv2.leftPanel.background};
  ${customSlimScroll}
`;

const TimeValue = styled.button<{ isSelected: boolean }>`
  width: 100%;
  padding: 8px 12px;
  border: none;
  background: ${({ isSelected, theme }) =>
    isSelected ? theme.datePickerv2.timePicker.selected.background : 'transparent'};
  color: ${({ isSelected, theme }) =>
    isSelected
      ? theme.datePickerv2.timePicker.selected.color
      : theme.datePickerv2.timePicker.default.color};
  cursor: pointer;
  font-size: 14px;
  text-align: center;

  &:hover {
    background: ${({ isSelected, theme }) =>
      isSelected
        ? theme.datePickerv2.timePicker.selected.background
        : theme.datePickerv2.timePicker.default.hoverBackground};
  }

  &:first-child {
    border-top-left-radius: 4px;
    border-top-right-radius: 4px;
  }

  &:last-child {
    border-bottom-left-radius: 4px;
    border-bottom-right-radius: 4px;
  }
`;

const ErrorMessage = styled.div`
  color: ${({ theme }) => theme.datePickerv2.error};
  font-size: 12px;
  margin-top: 4px;
`;

export const TimePicker: React.FC<TimePickerProps> = ({
  value,
  onChange,
  timeFormat = '24h',
  label,
  includeSeconds = false,
}) => {
  const [inputValue, setInputValue] = useState(value);
  const [error, setError] = useState<string>('');

  // Sync inputValue with value prop
  useEffect(() => {
    setInputValue(value);
  }, [value]);

  // Parse time string to components
  const parseTime = useCallback((timeStr: string) => {
    const parts = timeStr.split(':');
    return {
      hours: parseInt(parts[0] || '0', 10),
      minutes: parseInt(parts[1] || '0', 10),
      seconds: parseInt(parts[2] || '0', 10),
    };
  }, []);

  // Format time components to string
  const formatTime = useCallback((hours: number, minutes: number, seconds: number) => {
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds
      .toString()
      .padStart(2, '0')}`;
  }, []);

  // Handle time picker selection
  const handleTimeSelect = useCallback(
    (type: 'hours' | 'minutes' | 'seconds', value: number) => {
      const current = parseTime(inputValue);
      let newTime: string;

      if (type === 'hours') {
        newTime = formatTime(value, current.minutes, current.seconds);
      } else if (type === 'minutes') {
        newTime = formatTime(current.hours, value, current.seconds);
      } else {
        newTime = formatTime(current.hours, current.minutes, value);
      }

      setInputValue(newTime);
      onChange(newTime);
      setError('');
    },
    [inputValue, parseTime, formatTime, onChange],
  );

  // Generate time values based on format
  const getHours = () => {
    if (timeFormat === '12h') {
      return Array.from({ length: 12 }, (_, i) => i + 1); // 1-12
    }
    return Array.from({ length: 24 }, (_, i) => i); // 0-23
  };

  const hours = getHours();
  const minutes = Array.from({ length: 60 }, (_, i) => i);
  const seconds = Array.from({ length: 60 }, (_, i) => i);

  const currentTime = parseTime(inputValue);

  // Format hour display
  const formatHourDisplay = (hour: number) => {
    if (timeFormat === '12h') {
      return hour.toString();
    }
    return hour.toString().padStart(2, '0');
  };

  return (
    <TimeInputContainer>
      {label && <span>{label}</span>}

      <TimeColumns>
        <TimeColumn>
          <ColumnLabel>Hour</ColumnLabel>
          <TimeValues>
            {hours.map((hour) => (
              <TimeValue
                key={hour}
                isSelected={currentTime.hours === hour}
                onClick={() => handleTimeSelect('hours', hour)}
              >
                {formatHourDisplay(hour)}
              </TimeValue>
            ))}
          </TimeValues>
        </TimeColumn>

        <TimeColumn>
          <ColumnLabel>Min</ColumnLabel>
          <TimeValues>
            {minutes.map((minute) => (
              <TimeValue
                key={minute}
                isSelected={currentTime.minutes === minute}
                onClick={() => handleTimeSelect('minutes', minute)}
              >
                {minute.toString().padStart(2, '0')}
              </TimeValue>
            ))}
          </TimeValues>
        </TimeColumn>

        {includeSeconds && (
          <TimeColumn>
            <ColumnLabel>Second</ColumnLabel>
            <TimeValues>
              {seconds.map((second) => (
                <TimeValue
                  key={second}
                  isSelected={currentTime.seconds === second}
                  onClick={() => handleTimeSelect('seconds', second)}
                >
                  {second.toString().padStart(2, '0')}
                </TimeValue>
              ))}
            </TimeValues>
          </TimeColumn>
        )}
      </TimeColumns>

      {error && <ErrorMessage>{error}</ErrorMessage>}
    </TimeInputContainer>
  );
};
